import React from 'react';
import { RefreshCw, Users, HomeIcon, SearchIcon, Shield, Clock, Sparkles, Target, CheckCircle } from '../../icons';
import Button from '../ui/Button';
import StatTile from '../ui/StatTile';
import { CardSection } from '../ui/Card';
import ErrorDisplay from '../common/ErrorDisplay';
import { cn } from '../ui/cn';

const AnalyticsDashboard = ({
  globalStats,
  cacheMetrics,
  isLoading,
  isRecalculating,
  error,
  onRecalculate
}) => {
  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleRecalculate = async () => {
    try {
      await onRecalculate();
    } catch (err) {
      // Error is handled by the hook
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" aria-busy="true" aria-label="Loading analytics">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-20 bg-card border border-line rounded-card animate-pulse" />
        ))}
      </div>
    );
  }

  const fmt = (v) => (v ?? 0).toLocaleString();
  const perUser = (n) => (globalStats.totalUsers > 0 ? (n / globalStats.totalUsers).toFixed(1) : '0');

  return (
    <div className="space-y-4">
      {/* Header with Recalculate Button */}
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-secondary">
          {globalStats.lastUpdated ? <>Last updated {formatDate(globalStats.lastUpdated)}</> : <>Stats not yet calculated.</>}
        </p>
        <Button variant="secondary" size="sm" onClick={handleRecalculate} disabled={isRecalculating} loading={isRecalculating}>
          {!isRecalculating && <RefreshCw className="w-4 h-4" />}
          {isRecalculating ? 'Recalculating…' : 'Recalculate'}
        </Button>
      </div>

      <ErrorDisplay error={error} />

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile icon={Users} label="Users" value={fmt(globalStats.totalUsers)} accent />
        <StatTile icon={Shield} label="Admins" value={fmt(globalStats.totalAdmins)} />
        <StatTile icon={HomeIcon} label="Properties" value={fmt(globalStats.totalProperties)} />
        <StatTile icon={SearchIcon} label="Searches" value={fmt(globalStats.totalSearches)} />
      </div>

      {/* Cache Metrics */}
      {cacheMetrics && <CacheMetricsCard cacheMetrics={cacheMetrics} formatDate={formatDate} />}

      {/* Additional Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <CardSection title="Averages">
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between items-center">
              <dt className="text-secondary">Properties per user</dt>
              <dd className="text-primary font-medium tabular">{perUser(globalStats.totalProperties)}</dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-secondary">Searches per user</dt>
              <dd className="text-primary font-medium tabular">{perUser(globalStats.totalSearches)}</dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-secondary">Admin ratio</dt>
              <dd className="text-primary font-medium tabular">
                {globalStats.totalUsers > 0 ? ((globalStats.totalAdmins / globalStats.totalUsers) * 100).toFixed(1) : '0'}%
              </dd>
            </div>
          </dl>
        </CardSection>

        <CardSection title="System info">
          <div className="space-y-2 text-sm text-secondary">
            <div className="flex items-center gap-2.5">
              <Clock className="w-4 h-4 text-muted" />
              <span>Stats are calculated on-demand</span>
            </div>
            <div className="flex items-center gap-2.5">
              <RefreshCw className="w-4 h-4 text-muted" />
              <span>Click Recalculate to refresh counts</span>
            </div>
            <p className="text-xs text-muted pt-2">
              For real-time counters, enable Cloud Functions (Phase 4+). Current implementation uses on-demand calculation to minimize costs.
            </p>
          </div>
        </CardSection>
      </div>
    </div>
  );
};

const CacheMetricsCard = ({ cacheMetrics, formatDate }) => {
  const { totalHits, totalMisses, hitRate, lastUpdated } = cacheMetrics;
  const hitRatePercent = (hitRate * 100).toFixed(1);

  const hitRateColor = hitRate > 0.5 ? 'text-accent' : hitRate >= 0.2 ? 'text-amber' : 'text-danger';

  return (
    <CardSection
      title={
        <span className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-accent" /> AI cache performance
        </span>
      }
      action={lastUpdated && <span className="text-xs text-muted">Updated {formatDate(lastUpdated)}</span>}
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-page border border-line rounded-xl px-3 py-2.5">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted">
            <Target className="w-3.5 h-3.5" /> Cache hit rate
          </div>
          <div className={cn('mt-0.5 text-[17px] font-semibold tabular', hitRateColor)}>{hitRatePercent}%</div>
        </div>
        <StatTile icon={CheckCircle} label="AI calls saved" value={(totalHits ?? 0).toLocaleString()} />
        <StatTile icon={Sparkles} label="Cache writes" value={(totalMisses ?? 0).toLocaleString()} />
      </div>
    </CardSection>
  );
};

export default AnalyticsDashboard;
